const USER_STORAGE_KEY = "user";

export interface StoredUser {
  id?: number | string;
  username?: string;
  display_name?: string;
  [key: string]: unknown;
}

const getSafeStorage = (): Storage | null => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const toStringId = (value: unknown): string | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(Math.trunc(value));
  }
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  return null;
};

let memoryUser: StoredUser | null = null;

export const getStoredUser = (): StoredUser | null => {
  const storage = getSafeStorage();
  if (!storage) {
    return memoryUser;
  }
  try {
    const raw = storage.getItem(USER_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as StoredUser;
    if (parsed && typeof parsed === "object") {
      return parsed;
    }
  } catch (error) {
    console.warn("getStoredUser: failed to parse user payload", error);
  }
  return null;
};

export const getStoredUserId = (): string | null => {
  const user = getStoredUser();
  if (!user) {
    return null;
  }
  return toStringId(user.id);
};

export const setStoredUser = (user: StoredUser | null): void => {
  const storage = getSafeStorage();
  memoryUser = user;
  if (!storage) {
    return;
  }
  if (!user) {
    storage.removeItem(USER_STORAGE_KEY);
    return;
  }
  try {
    storage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  } catch (error) {
    console.warn("setStoredUser: failed to persist user payload", error);
  }
};

export const clearStoredUser = (): void => {
  setStoredUser(null);
};

export const hasSessionCookie = (): boolean => {
  if (typeof document === "undefined") {
    return false;
  }
  return document.cookie
    .split(";")
    .map((item) => item.trim())
    .some((item) => item.toLowerCase().startsWith("session=") && item.length > "session=".length);
};

export { USER_STORAGE_KEY };
